import type { GradeReport, SectionScore, Rubric, RubricGradeResult, GenerateParams, GeneratedReport } from '../types';
import type { ParsedFile } from './fileParser';
import { api } from './api';

const USE_MOCKS = import.meta.env.VITE_USE_MOCKS !== 'false';

/* ----------------------------- Grading ------------------------------- */

export async function gradeReport(parsed: ParsedFile): Promise<GradeReport> {
  if (!USE_MOCKS) {
    try {
      const { data } = await api.post<GradeReport>('/grade', { parsed });
      return data;
    } catch (err: any) {
      throw humanizeApiError(err);
    }
  }
  await wait(900 + Math.random() * 600);
  return mockGrade(parsed);
}

export async function gradeByRubric(parsed: ParsedFile, rubric: Rubric): Promise<RubricGradeResult> {
  if (!USE_MOCKS) {
    try {
      const { data } = await api.post<RubricGradeResult>('/grade/rubric', { parsed, rubric });
      return data;
    } catch (err: any) {
      throw humanizeApiError(err);
    }
  }
  await wait(900 + Math.random() * 600);
  return mockRubricResult(parsed, rubric);
}

export async function generateReport(params: GenerateParams): Promise<GeneratedReport> {
  if (!USE_MOCKS) {
    try {
      const { data } = await api.post<GeneratedReport>('/generate', params);
      // The backend is the source of truth for shape, but we validate here so a
      // mis-shaped response (e.g. an LLM that ignored the JSON schema, or a
      // mock fallback with no API key) fails loudly instead of rendering a
      // blank preview and a broken download button.
      if (!data || typeof data.markdown !== 'string' || typeof data.title !== 'string') {
        const anyData = data as any;
        // eslint-disable-next-line no-console
        console.error('[generateReport] unexpected response', anyData);
        if (anyData?.error) throw new Error(String(anyData.error));
        if (anyData?.mock) {
          throw new Error(
            'Backend is running in mock mode — configure OPENAI_API_KEY (or ANTHROPIC_API_KEY) on the server.'
          );
        }
        // HTML response means we fell through the SPA rewrite — misrouted request.
        if (typeof anyData === 'string' && /^\s*<!doctype html/i.test(anyData)) {
          throw new Error(
            'API request was misrouted (got HTML instead of JSON). Check VITE_API_BASE_URL and the Firebase Hosting /api/** rewrite.'
          );
        }
        let preview: string;
        try {
          preview = typeof anyData === 'string' ? anyData : JSON.stringify(anyData);
        } catch { preview = String(anyData); }
        throw new Error(
          `Generation returned an unexpected response shape. Server sent: ${preview.slice(0, 200)}`
        );
      }
      return data;
    } catch (err: any) {
      throw humanizeApiError(err);
    }
  }
  await wait(1400 + Math.random() * 800);
  return mockGenerated(params);
}

/** Turn backend rate-limit / quota errors into user-facing messages. */
function humanizeApiError(err: any): Error {
  // Pass through errors we already surfaced ourselves (e.g. shape validation).
  if (err instanceof Error && !(err as any).response) return err;
  const status = err?.response?.status;
  const body = err?.response?.data ?? {};
  if (status === 429) {
    return new Error(body.error ?? 'Too many requests. Please wait a moment and try again.');
  }
  if (status === 402) {
    return new Error(body.message ?? body.error ?? 'Out of credits — top up at /pricing.');
  }
  if (status === 401) return new Error('Please sign in again.');
  return new Error(body.error ?? err?.message ?? 'Request failed');
}

/* ----------------------------- Helpers ------------------------------- */

function wait(ms: number) { return new Promise((r) => setTimeout(r, ms)); }

function scoreFromText(text: string, floor = 55): number {
  const words = text.split(/\s+/).filter(Boolean).length;
  const lexical = Math.min(1, new Set(text.toLowerCase().split(/\W+/)).size / Math.max(words, 1) * 2);
  const lengthBoost = Math.min(1, words / 400);
  const noise = (Math.random() - 0.5) * 10;
  return clamp(Math.round(floor + 35 * (0.6 * lengthBoost + 0.4 * lexical) + noise), 30, 100);
}

function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }

function letter(n: number) {
  if (n >= 93) return 'A';
  if (n >= 90) return 'A-';
  if (n >= 87) return 'B+';
  if (n >= 83) return 'B';
  if (n >= 80) return 'B-';
  if (n >= 77) return 'C+';
  if (n >= 73) return 'C';
  if (n >= 70) return 'C-';
  if (n >= 60) return 'D';
  return 'F';
}

function mockGrade(p: ParsedFile): GradeReport {
  const defaultSections = [
    'Abstract', 'Introduction', 'Methodology', 'Results', 'Discussion', 'Conclusion', 'References',
  ];
  const present = new Set(p.sections.map((s) => s.name.toLowerCase()));
  const sections: SectionScore[] = defaultSections.map((name) => {
    const match = p.sections.find((s) => s.name.toLowerCase().includes(name.toLowerCase()));
    const score = match ? scoreFromText(match.content, 60) : 45;
    return {
      name,
      score,
      strengths: match ? strengthsFor(name, score) : [],
      weaknesses: !match
        ? [`No clearly labeled ${name} section was detected.`]
        : weaknessesFor(name, score),
      recommendedActions: recsFor(name, score, Boolean(match)),
      evidenceQuotes: match ? [snippet(match.content)] : [],
    };
  });
  const overall = Math.round(sections.reduce((a, s) => a + s.score, 0) / sections.length);
  const criteria = [
    { name: 'Structure & organization', weight: 15, score: scoreAround(overall) },
    { name: 'Clarity of writing',       weight: 15, score: scoreAround(overall) },
    { name: 'Depth of analysis',        weight: 20, score: scoreAround(overall - 3) },
    { name: 'Evidence & citations',     weight: 20, score: scoreAround(overall - 5) },
    { name: 'Originality',              weight: 10, score: scoreAround(overall) },
    { name: 'Language mechanics',       weight: 10, score: scoreAround(overall + 4) },
    { name: 'Formatting & presentation',weight: 10, score: scoreAround(overall) },
  ];
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    fileName: p.fileName,
    overallScore: overall,
    letterGrade: letter(overall),
    summary: `Your report spans ${p.wordCount.toLocaleString()} words across ${p.sections.length} detected section(s). Overall writing is solid, with the strongest performance in structure and the biggest lift available in evidence and depth of analysis.`,
    sections,
    criteriaBreakdown: criteria,
    improvementPlan: [
      'Add 2–3 additional primary sources and cite them inline in Methodology and Results.',
      'Tighten the Introduction: state the research question and your hypothesis in the first paragraph.',
      'In Discussion, explicitly address at least one counter-argument or limitation before concluding.',
      'Replace passive voice ("was observed") with active voice to raise Clarity.',
      'Normalize heading style (all H2 or all H1 for sections) to improve Formatting & presentation.',
    ],
  };
}

function scoreAround(n: number) { return clamp(Math.round(n + (Math.random() * 10 - 5)), 40, 100); }

function snippet(text: string) {
  const sentences = text.split(/(?<=[.!?])\s+/).filter((s) => s.length > 40 && s.length < 220);
  return sentences[Math.min(1, sentences.length - 1)] ?? text.slice(0, 140) + '…';
}

const strengthsLib: Record<string, string[]> = {
  Abstract: ['Concise framing of the research question.', 'Clear articulation of the contribution.'],
  Introduction: ['Sets context well with recent related work.', 'Motivation is compelling.'],
  Methodology: ['Procedure is reproducible.', 'Variables and controls are named.'],
  Results: ['Figures are described in text.', 'Uses appropriate statistics.'],
  Discussion: ['Links findings to prior work.', 'Identifies practical implications.'],
  Conclusion: ['Neatly summarizes contributions.', 'Ends with a clear takeaway.'],
  References: ['Consistent citation style.', 'Recent and relevant sources.'],
};
const weaknessesLib: Record<string, string[]> = {
  Abstract: ['No quantified result is stated.', 'Method is only implied.'],
  Introduction: ['Research gap is not explicitly named.', 'Thesis statement is buried.'],
  Methodology: ['Sample size and selection criteria are unclear.', 'No validity/threats section.'],
  Results: ['Some claims lack supporting numbers.', 'No uncertainty/CIs reported.'],
  Discussion: ['Counter-arguments are not engaged.', 'Limitations are under-specified.'],
  Conclusion: ['Restates Introduction rather than contributions.', 'No future-work direction.'],
  References: ['Inconsistent formatting of DOIs.', 'A few outdated (>10 yrs) sources.'],
};

function strengthsFor(name: string, score: number): string[] {
  const base = strengthsLib[name] ?? ['Reads fluently.', 'Organized paragraphs.'];
  return score >= 80 ? base : base.slice(0, 1);
}
function weaknessesFor(name: string, score: number): string[] {
  const base = weaknessesLib[name] ?? ['Some long sentences hurt readability.'];
  return score >= 85 ? base.slice(0, 1) : base;
}
function recsFor(name: string, score: number, present: boolean): string[] {
  if (!present) return [`Add a dedicated ${name} section with the required content.`];
  if (score >= 90) return ['Tighten for concision — minor polish only.'];
  if (score >= 80) return [`Add one concrete example or figure reference to ${name}.`];
  return [
    `Rework ${name} to address the weaknesses above.`,
    'Add 1–2 citations supporting the main claims in this section.',
  ];
}

/* ----------------------------- Rubric ------------------------------- */

function mockRubricResult(p: ParsedFile, rubric: Rubric): RubricGradeResult {
  const results = rubric.criteria.map((c) => {
    const raw = scoreFromText(p.text, 55) / 100;
    const score = Math.round(raw * c.max * 10) / 10;
    return {
      criterionId: c.id,
      criterionName: c.name,
      score,
      maxScore: c.max,
      weight: c.weight,
      feedback: `On "${c.name}", the report ${raw > 0.75 ? 'meets expectations with clear evidence' : raw > 0.6 ? 'partially meets expectations — some claims need support' : 'falls short — add explicit evidence and restructure'}.`,
      evidenceQuotes: [snippet(p.text)],
    };
  });
  const weightedPct = results.reduce((a, r) => a + (r.score / r.maxScore) * r.weight, 0) / 100;
  const total = Math.round(weightedPct * (rubric.scaleMax - rubric.scaleMin) + rubric.scaleMin);
  return {
    rubricName: rubric.name,
    totalScore: total,
    letterGrade: letter(Math.round(weightedPct * 100)),
    criteriaResults: results,
    overallFeedback: `Against the "${rubric.name}" rubric, this report scores ${total}/${rubric.scaleMax}. The strongest criteria are structural; the biggest gains are available in evidence and critical discussion.`,
  };
}

/* ----------------------------- Generation ------------------------------- */

function mockGenerated(params: GenerateParams): GeneratedReport {
  const sections = params.sections?.length
    ? params.sections
    : ['Introduction', 'Background', 'Analysis', 'Recommendations', 'Conclusion'];
  const perSection = Math.max(120, Math.round(params.lengthWords / sections.length));
  const title = params.topic.replace(/^\w/, (c) => c.toUpperCase());
  const body = sections.map((s) => `## ${s}\n\n${paragraph(params.topic, s, perSection, params.tone)}`).join('\n\n');
  const md = `# ${title}\n\n*Audience: ${params.audience} · Tone: ${params.tone} · ~${params.lengthWords} words*\n\n${body}\n`;
  return { title, markdown: md, wordCount: md.split(/\s+/).length };
}

function paragraph(topic: string, section: string, targetWords: number, tone: string) {
  const openers: Record<string, string[]> = {
    academic: ['Prior work suggests', 'Empirical evidence indicates', 'A growing body of research demonstrates'],
    business: ['Industry data show', 'Market dynamics suggest', 'Leading organizations have adopted'],
    casual:   ['Think of it this way:', "Here's the gist:", "Let's be real —"],
    technical:['From a systems perspective,', 'Architecturally,', 'At runtime,'],
  };
  const opener = (openers[tone] ?? openers.academic)[Math.floor(Math.random() * 3)];
  let text = `${opener} ${topic} intersects with ${section.toLowerCase()} in meaningful ways. `;
  const filler = `In the context of ${section.toLowerCase()}, the main considerations surrounding ${topic} include stakeholder incentives, technical feasibility, and measurable outcomes. This section examines each of these dimensions in turn, synthesizing recent findings and practical heuristics.`;
  while (text.split(/\s+/).length < targetWords) text += ' ' + filler;
  return text.slice(0, text.lastIndexOf('. ') + 1);
}
